import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api, { downloadFile, getErrorMessage } from "@/lib/api";
import { exactMoney } from "@/lib/exactMoney";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UploadDialog } from "@/pages/modules/EvidencePanel";
import NativeWordHistory from "@/pages/modules/NativeWordHistory";
import { PortalReminders } from "@/pages/modules/ComponentReminders";
import { SectionCard, StatusBadge, MetricCard, AlertBanner, EmptyState } from "@/components/ui/design";
import { LogOut, Download, Upload, FolderOpenDot, FileCheck, MessageSquare, ShieldCheck, ClipboardList } from "lucide-react";

const DASH_ITEMS = [
  ["instructions", "Instruksi", ClipboardList], ["independence", "Independensi", ShieldCheck],
  ["materiality", "Materialitas", FileCheck], ["procedures", "Prosedur", ClipboardList],
  ["deliverables", "Deliverables", Upload], ["findings", "Temuan", MessageSquare],
  ["completion", "Penyelesaian", FileCheck],
];

export default function ComponentPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [comps, setComps] = useState(null);
  const [sel, setSel] = useState(null);

  const load = useCallback(() => api.get("/portal/my-components").then((r) => { setComps(r.data); setSel((s) => s ? r.data.find((c) => c.id === s.id) || r.data[0] : r.data[0]); }), []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-[#0B2038] text-white px-6 py-3 flex items-center justify-between shadow-card-lg">
        <div>
          <div className="font-head text-lg font-bold tracking-tight">AUDIT OS · Portal Auditor Komponen</div>
          <div className="text-[10px] uppercase tracking-widest text-sky-300/80">Akses terbatas pada komponen yang ditugaskan</div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right"><div className="text-xs font-medium">{user?.name}</div><div className="text-[11px] text-sky-300/80">{user?.role}</div></div>
          <button onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-1.5 text-[12px] text-slate-300 hover:text-white" data-testid="portal-logout"><LogOut size={14} />Keluar</button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6 grid lg:grid-cols-[260px_1fr] gap-6" data-testid="component-portal">
        <aside className="space-y-2">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground px-1">Komponen Saya</div>
          {comps === null && <div className="text-sm text-muted-foreground">Memuat...</div>}
          {comps && comps.length === 0 && <div className="text-xs text-muted-foreground p-3 section-card">Belum ada komponen yang ditugaskan.</div>}
          {(comps || []).map((c) => (
            <button key={c.id} onClick={() => setSel(c)} data-testid={`portal-comp-${c.id}`}
              className={`w-full text-left section-card px-3 py-2.5 transition-colors ${sel?.id === c.id ? "ring-2 ring-navy-500 border-navy-500" : "hover:border-navy-300"}`}>
              <div className="flex items-center gap-2"><FolderOpenDot size={15} className="text-navy-700 shrink-0" /><span className="text-sm font-medium truncate">{c.entity}</span></div>
              <div className="text-[11px] text-muted-foreground truncate mt-0.5">{c.client_name}</div>
              <div className="mt-1"><StatusBadge tone={c.portal_status === "Cleared" ? "success" : c.portal_status === "Follow-up Required" ? "warning" : "info"} icon={false}>{c.portal_status}</StatusBadge></div>
            </button>
          ))}
        </aside>

        {sel ? <ComponentDetail comp={sel} reload={load} /> : (comps && comps.length > 0 ? null : <EmptyState icon={FolderOpenDot} title="Tidak ada penugasan" description="Hubungi tim audit grup untuk penugasan komponen." />)}
      </div>
    </div>
  );
}

function ComponentDetail({ comp, reload }) {
  const [evidence, setEvidence] = useState([]);
  const [notes, setNotes] = useState([]);
  const [meta, setMeta] = useState({ workflow: [], deliverable_types: [] });
  const [dlg, setDlg] = useState(false);
  const load = useCallback(() => {
    api.get(`/portal/components/${comp.id}/evidence`).then((r) => setEvidence(r.data));
    api.get(`/portal/components/${comp.id}/review-notes`).then((r) => setNotes(r.data));
  }, [comp.id]);
  useEffect(() => { load(); api.get("/portal/meta").then((r) => setMeta(r.data)); }, [load]);

  const setStatus = async (status, sign_off = false) => {
    try { await api.post(`/portal/components/${comp.id}/status`, { status, sign_off }); toast.success(`Status: ${status}`); reload(); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };
  const respond = async (nid) => {
    const response = window.prompt("Tanggapan atas catatan reviu:"); if (!response) return;
    await api.post(`/portal/components/${comp.id}/review-notes/${nid}/respond`, { response }); toast.success("Tanggapan terkirim"); load();
  };
  const d = comp.dashboard || {};
  const openNotes = notes.filter((n) => n.status !== "Cleared").length;

  return (
    <div className="space-y-5 animate-in-up">
      <SectionCard title={comp.entity} subtitle={[comp.engagement_name, comp.component_type, comp.scope].map(v => v || "Belum tersedia").join(" · ")} icon={FolderOpenDot}
        actions={<StatusBadge tone={comp.portal_status === "Cleared" ? "success" : comp.portal_status === "Follow-up Required" ? "warning" : "info"}>{comp.portal_status}</StatusBadge>}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard label="Materialitas Komponen" value={<span className="block break-all text-sm">{exactMoney(comp.component_materiality)}</span>} testid="portal-materiality" />
          <MetricCard label="Signifikansi" value={comp.significance || "—"} />
          <MetricCard label="Independensi" value={comp.independence_confirmed ? "Dikonfirmasi" : "Belum"} tone={comp.independence_confirmed ? "success" : "warning"} />
          <MetricCard label="Catatan Terbuka" value={openNotes} tone={openNotes ? "warning" : "success"} />
        </div>
      </SectionCard>

      {/* progress dashboard */}
      <div className="grid grid-cols-3 md:grid-cols-7 gap-2" data-testid="portal-dashboard">
        {DASH_ITEMS.map(([k, label, Icon]) => (
          <div key={k} className={`section-card p-2.5 text-center ${d[k] ? "border-green-300 bg-green-50/50" : ""}`}>
            <Icon size={16} className={`mx-auto mb-1 ${d[k] ? "text-green-600" : "text-slate-400"}`} />
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
          </div>
        ))}
      </div>

      <SectionCard title="Instruksi Audit Grup" icon={ClipboardList}
        actions={<Button size="sm" variant="outline" onClick={() => downloadFile(`/portal/components/${comp.id}/instructions/download`, `Instruksi_${comp.entity}.docx`)} data-testid="portal-instr-dl"><Download size={13} className="mr-1" />Unduh .docx</Button>}>
        <div className="space-y-2 text-sm">
          <div><span className="text-muted-foreground text-xs uppercase tracking-wider">Instruksi</span><p className="mt-0.5">{comp.instructions_text || "—"}</p></div>
          <div><span className="text-muted-foreground text-xs uppercase tracking-wider">Prosedur yang Diminta</span><p className="mt-0.5">{comp.required_procedures || "—"}</p></div>
          <div><span className="text-muted-foreground text-xs uppercase tracking-wider">Risiko Signifikan</span><p className="mt-0.5">{comp.significant_risks_note || "—"}</p></div>
        </div>
        <div className="mt-3 min-w-0" data-testid="portal-word-history">
          <NativeWordHistory id={comp.id} exportType="COMPONENT_INSTRUCTIONS" recordId={comp.id}
            historyPath={`/portal/components/${comp.id}/word-exports`} templates={false} />
        </div>
      </SectionCard>

      <SectionCard title="Deliverables & Bukti Komponen" icon={Upload}
        actions={<Button size="sm" onClick={() => setDlg(true)} data-testid="portal-upload"><Upload size={13} className="mr-1" />Unggah Deliverable</Button>}>
        {evidence.length === 0 ? <div className="text-xs text-muted-foreground py-2">Belum ada deliverable/bukti diunggah.</div> : (
          <div className="space-y-1">
            {evidence.map((e) => (
              <div key={e.id} className="flex items-center gap-2 border border-border rounded-md px-3 py-2 bg-white" data-testid={`portal-ev-${e.id}`}>
                <FileCheck size={14} className="text-navy-700" />
                <span className="text-sm flex-1 truncate">{e.filename}</span>
                <span className="text-[11px] text-muted-foreground">{e.deliverable_type || e.category || "—"}</span>
                <Button size="sm" variant="ghost" className="h-6 px-1" onClick={() => downloadFile(`/portal/evidence/${e.id}/download`, e.original_filename || e.filename)}><Download size={13} /></Button>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <PortalReminders cid={comp.id} />

      <SectionCard title="Catatan Reviu Auditor Grup" icon={MessageSquare}>
        {notes.length === 0 ? <div className="text-xs text-muted-foreground py-2">Belum ada catatan reviu.</div> : (
          <div className="space-y-2">
            {notes.map((n) => (
              <div key={n.id} className="border border-border rounded-md p-3 bg-white" data-testid={`portal-note-${n.id}`}>
                <div className="flex items-center gap-2"><StatusBadge tone={n.status === "Cleared" ? "success" : n.status === "Responded" ? "info" : "warning"}>{n.status}</StatusBadge><span className="text-[11px] text-muted-foreground">oleh {n.raised_by}</span></div>
                <p className="text-sm mt-1.5">{n.note}</p>
                {n.response && <p className="text-[13px] mt-1 pl-3 border-l-2 border-navy-300 text-muted-foreground">Tanggapan: {n.response}</p>}
                {n.status !== "Cleared" && <Button size="sm" variant="outline" className="mt-2 h-7 text-[11px]" onClick={() => respond(n.id)} data-testid={`portal-respond-${n.id}`}>Tanggapi</Button>}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard title="Alur Kerja & Sign-off Komponen">
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setStatus("Acknowledged")} data-testid="portal-ack">Konfirmasi Terima</Button>
          <Button size="sm" variant="outline" onClick={() => setStatus("Component Work In Progress")}>Mulai Pekerjaan</Button>
          <Button size="sm" variant="outline" onClick={() => setStatus("Submitted")} data-testid="portal-submit">Ajukan Paket</Button>
          <Button size="sm" variant="outline" onClick={() => setStatus("Resubmitted")}>Ajukan Ulang</Button>
          <Button size="sm" onClick={() => setStatus("Submitted", true)} data-testid="portal-signoff"><ShieldCheck size={13} className="mr-1" />Sign-off Komponen</Button>
        </div>
        <div className="text-[11px] text-muted-foreground mt-2">Reviu grup & status "Cleared" ditentukan oleh Auditor Grup.</div>
      </SectionCard>

      <UploadDialog open={dlg} onOpenChange={setDlg} eid={comp.engagement_id}
        defaults={{ phase: "Audit Grup", module: "ComponentPackage", component_id: comp.id, category: "Deliverable Komponen" }}
        lockContext={true} onDone={() => { setDlg(false); load(); reload(); if (comp.portal_status === "Sent" || comp.portal_status === "Acknowledged") setStatus("Component Work In Progress"); }} />
    </div>
  );
}
